import React, { useState } from "react";
import {
    AppBar,
    Toolbar,
    Typography,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    RadioGroup,
    FormControlLabel,
    Radio,
    FormLabel,
    Stack,
    Divider,
    Box,
    Chip,
    alpha,
    Tooltip,
    TextField,
    Alert,
} from "@mui/material";
import SettingsIcon from "@mui/icons-material/Settings";
import MenuIcon from "@mui/icons-material/Menu";
import PaletteIcon from "@mui/icons-material/Palette";
import { useThemeSettings } from "../theme";
import { useAuth } from "../auth/AuthContext";

const Header = ({ onMenuClick }) => {
    const { themeId, setThemeId, choices } = useThemeSettings();
    const { user, logout, updateProfile, changePassword } = useAuth();
    const [settingsOpen, setSettingsOpen] = useState(false);
    const [displayName, setDisplayName] = useState("");
    const [currentPassword, setCurrentPassword] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [settingsMessage, setSettingsMessage] = useState("");
    const activeTheme = choices.find((c) => c.id === themeId);

    const openSettings = () => {
        setDisplayName(user?.display_name || "");
        setCurrentPassword("");
        setNewPassword("");
        setSettingsMessage("");
        setSettingsOpen(true);
    };

    const saveAccount = async () => {
        try {
            await updateProfile(displayName);
            if (newPassword) {
                await changePassword(currentPassword, newPassword);
                return;
            }
            setSettingsMessage("Account settings saved.");
        } catch (error) {
            setSettingsMessage(error.response?.data?.error || "Unable to save account settings.");
        }
    };

    const todayLabel = new Date().toLocaleDateString(undefined, {
        weekday: "long",
        month: "short",
        day: "numeric",
    });

    return (
        <>
            <AppBar position="static" elevation={0}>
                <Toolbar sx={{ justifyContent: "space-between", minHeight: 56, px: { xs: 2, sm: 2.5 } }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 1, sm: 2 }, minWidth: 0 }}>
                        <IconButton
                            onClick={onMenuClick}
                            size="small"
                            sx={{ display: { xs: "inline-flex", md: "none" }, color: "text.secondary", flexShrink: 0 }}
                            aria-label="Open navigation"
                        >
                            <MenuIcon />
                        </IconButton>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                            {todayLabel}
                        </Typography>
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 0.5, sm: 1 }, flexShrink: 0 }}>
                        {user ? (
                            <Tooltip title="Account settings">
                                <Button
                                    size="small"
                                    color="inherit"
                                    onClick={openSettings}
                                    sx={{ color: "text.secondary", textTransform: "none", minHeight: 44 }}
                                >
                                    <Box component="span" sx={{ display: { xs: "none", sm: "inline" } }}>{user.display_name}</Box>
                                    <Box component="span" sx={{ display: { xs: "inline", sm: "none" } }}>Account</Box>
                                </Button>
                            </Tooltip>
                        ) : null}
                        {activeTheme ? (
                            <Chip
                                size="small"
                                icon={<PaletteIcon sx={{ fontSize: "16px !important" }} />}
                                label={activeTheme.name}
                                variant="outlined"
                                sx={{ display: { xs: "none", sm: "inline-flex" }, fontWeight: 600, height: 28 }}
                            />
                        ) : null}
                        <IconButton
                            onClick={openSettings}
                            size="small"
                            sx={(theme) => ({
                                color: "text.secondary",
                                border: 1,
                                borderColor: "divider",
                                borderRadius: 1.5,
                                "&:hover": {
                                    color: "primary.main",
                                    borderColor: alpha(theme.palette.primary.main, 0.35),
                                    bgcolor: alpha(theme.palette.primary.main, 0.06),
                                },
                            })}
                            aria-label="Settings"
                        >
                            <SettingsIcon fontSize="small" />
                        </IconButton>
                    </Box>
                </Toolbar>
            </AppBar>

            <Dialog open={settingsOpen} onClose={() => setSettingsOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ fontWeight: 700 }}>Settings</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ pt: 1 }}>
                        <Box>
                            <FormLabel sx={{ fontWeight: 700, color: "text.primary" }}>Account</FormLabel>
                            <Stack spacing={1.5} sx={{ mt: 1 }}>
                                <TextField size="small" label="Display name" value={displayName} onChange={(event) => setDisplayName(event.target.value)} />
                                <Typography variant="caption" color="text.secondary">{user?.email}</Typography>
                                <TextField size="small" label="Current password" type="password" value={currentPassword} onChange={(event) => setCurrentPassword(event.target.value)} />
                                <TextField size="small" label="New password (12+ characters)" type="password" value={newPassword} onChange={(event) => setNewPassword(event.target.value)} />
                                {settingsMessage && <Alert severity={settingsMessage.includes("saved") ? "success" : "error"}>{settingsMessage}</Alert>}
                                <Button variant="contained" onClick={saveAccount}>Save account</Button>
                            </Stack>
                        </Box>
                        <Divider />
                        <Box>
                            <FormLabel id="theme-choice-label" sx={{ fontWeight: 700, color: "text.primary" }}>
                                Appearance
                            </FormLabel>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, mt: 0.5 }}>
                                Theme applies across the app and is saved in this browser.
                            </Typography>
                            <RadioGroup
                                aria-labelledby="theme-choice-label"
                                value={themeId}
                                onChange={(e) => setThemeId(e.target.value)}
                            >
                                {choices.map((c) => (
                                    <FormControlLabel
                                        key={c.id}
                                        value={c.id}
                                        control={<Radio color="primary" />}
                                        label={
                                            <Box>
                                                <Typography variant="body1" fontWeight={600}>
                                                    {c.name}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {c.description}
                                                </Typography>
                                            </Box>
                                        }
                                        sx={{ alignItems: "flex-start", ml: 0, mb: 1 }}
                                    />
                                ))}
                            </RadioGroup>
                        </Box>
                        <Divider />
                        <Typography variant="caption" color="text.secondary">
                            Signed in as {user?.email || "authorized staff"}.
                        </Typography>
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ justifyContent: "space-between", px: 3, py: 2 }}>
                    <Button color="inherit" onClick={logout}>
                        Sign out
                    </Button>
                    <Button onClick={() => setSettingsOpen(false)} variant="contained" color="primary">
                        Done
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default Header;
